import api from './api';

// Talks to /api/finance/* — budget consolidation and control (MOD_7_FIN).
//
// CROSS-SITE BY CONSTRUCTION. Every other service in Mantle rides the
// x-site-code header; this one is deliberately answered across all sites,
// because "has everyone submitted?" is not a question about one depot.
export const financeService = {
  // { modules, sites, cells, scope }
  //   cells: [{ site_code, module_code, in_scope, line_count, global_line_count,
  //             submitted, submitted_at, submitted_by, last_edit_at, stale }]
  //   scope: { gl_line_count, modules_on_map, modules_available, sites_on_map,
  //            sites_active }
  //
  // The axes are the version's GL BUDGET LINES, not the module and site
  // catalogues — a client with no depots has no Distribution column, not a
  // column that can never go green. `in_scope` false = nothing is budgeted
  // there, so it is out of every denominator.
  //
  // `stale` is the one worth the page: signed off, then edited. It reads as done
  // everywhere else in the product.
  getSubmissions: async () => {
    const res = await api.get('/finance/submissions');
    return res.data;
  },

  // The income statement. grain = the DETAIL INSIDE each section (accounts,
  // modules or sites); the sections themselves never change.
  //
  // { grain, structure: { sections, subtotals }, rows, unclassified, unposted }
  //
  // Amounts arrive in PRESENTATION SIGN — income positive, cost negative — so
  // every subtotal is the plain sum of the lines above it. The flip off the
  // debit-positive ledger happens once, server-side.
  getPnl: async (grain = 'account') => {
    const res = await api.get('/finance/pnl', { params: { grain } });
    return res.data;
  },

  // The indirect cash flow, group level (it takes no site parameter — cash is
  // managed centrally and sites do not have their own bank accounts).
  //
  // { structure: { sections, subtotals, balanceLines },
  //   rows:        [{ line_order, label, kind, section, financial_period, amount }],
  //   cash:        [{ financial_period, opening_cash, balance }],
  //   assumptions: { debtor_days, creditor_days, tax_rate, ... } | null,
  //   interest:    { perSchedule, inLedger } | null,
  //   daysSources, unposted }
  //
  // Amounts arrive with the CASH EFFECT already signed — in positive, out
  // negative — so every subtotal is the plain sum of its lines. `assumptions`
  // being null is the single most likely reason the statement reads as
  // nonsense, and the page says so rather than showing zeros.
  getCashFlow: async () => {
    const res = await api.get('/finance/cashflow');
    return res.data;
  },

  // The budgeted balance sheet, group level.
  //
  // { structure: { sections, groups, subtotals },
  //   rows:      [{ bs_section, financial_period, balance }],
  //   opening:   [{ bs_section, balance }],
  //   check:     [{ financial_period, total_assets, total_equity_and_liabilities, difference }],
  //   unclassified }
  //
  // Balances arrive in PRESENTATION SIGN: assets positive and equity and
  // liabilities ALSO positive, so both totals print positive and the check is
  // that they are equal. The flip off the debit-positive ledger happens once,
  // server-side, driven by bsStructure.DISPLAY_SIGN. Do not flip again here.
  //
  // `check.difference` is returned whether or not it is zero. Every line is
  // derived from its own driver rather than double-entered, so a residual is
  // normal — it is shown at the top of the page, never plugged.
  getBalanceSheet: async () => {
    const res = await api.get('/finance/balancesheet');
    return res.data;
  },

  // Working capital, and — the point of the endpoint — WHERE IT COMES FROM.
  //
  // { assumptions: [row per version x site, site NULL = group],
  //   components: [{ key, label, module, moduleLabel, route, driverLabel,
  //                  usesDays, driver[12], balance[12], daysUsed[12],
  //                  daysSources: [{ days_source, n, avg_days }] }] }
  //
  // Each component names the module that produces it — inventory from
  // Manufacturing, receivables from Revenue, payables from Procurement — and
  // says how much of its days figure came from a real contract rather than the
  // fallback assumption. `usesDays: false` on inventory is not an omission:
  // where there is a production plan the balance is COMPUTED and the days ratio
  // is never consulted.
  getWorkingCapital: async () => {
    const res = await api.get('/finance/working-capital');
    return res.data;
  },

  // Upsert one assumption row. Pass site_code: null for the group default.
  // An empty field saves as NULL, which means "not set" and falls back — it
  // does NOT mean zero, which would say every customer pays cash.
  saveWorkingCapital: async (payload) => {
    const res = await api.put('/finance/working-capital', payload);
    return res.data;
  },

  // Remove a SITE override. The group row cannot be deleted.
  deleteWorkingCapital: async (siteCode) => {
    const res = await api.delete(`/finance/working-capital/${encodeURIComponent(siteCode)}`);
    return res.data;
  },

  // The depreciation schedule — moved here from Capital, because what an asset
  // cost and how long it is written off over are financial policy, not
  // maintenance. Capital keeps the physical Asset Register.
  //
  // { items:    [one row per depreciable thing, ASSET or CAPEX],
  //   schedule: [{ item_kind, item_code, financial_period, budget_amount }],
  //   unusable: [assets that can never produce a charge, and why] }
  getDepreciation: async () => {
    const res = await api.get('/finance/depreciation');
    return res.data;
  },

  // An asset's FINANCIAL attributes only. Null on any field means "use the
  // class default" — that is what keeps a hundred-asset register maintainable.
  saveAssetFinancials: async (assetCode, payload) => {
    const res = await api.put(`/finance/depreciation/asset/${encodeURIComponent(assetCode)}`, payload);
    return res.data;
  },

  // grain: 'account' | 'module' | 'site'
  // { rows, bySource, unposted, excludes }
  //
  // `unposted` is cost that is budgeted and costed but has no GL account, so it
  // is absent from the total without any error. Shown before the total is.
  getConsolidated: async (grain = 'account') => {
    const res = await api.get('/finance/consolidated', { params: { grain } });
    return res.data;
  },
};

export default financeService;
